import { DataTable } from '@/components/ui/data-table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import PremiumUpsell from 'app/frontend/base/components/PremiumUpsell'
import {
  useEventRaidReports,
  useTeamEvent,
  useTeamEventRaidReportsMap,
} from 'app/frontend/base/config/queries'
import { useCallback, useMemo, useState } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import dayjs from 'dayjs'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import EventRaidReportsDialog from '../../TeamEvent/components/EventRaidReportsDialog'
import {
  eventDateColumn,
  eventRaidReportsColumn,
  eventReportActionsColumn,
  eventTimeColumn,
  eventTitleColumn,
} from '../../column-definitions/events'
import { useTeam } from 'app/frontend/base/config/context/team'
import { getAccessLevelForTeam } from 'app/frontend/base/config/permissions'
import { WCL_REPORT_RETRIEVAL_THRESHOLD } from 'app/frontend/base/config/wcl'
import { syncEventRaidReports } from 'app/frontend/base/api/event'
import {
  awaitSuccessfulJob,
  displayErrorToast,
} from 'app/frontend/base/helpers'
import { eventInstant } from 'app/frontend/base/helpers/eventInstant'

const DEFAULT_TITLE = 'Past events in scope'
const DEFAULT_DESCRIPTION =
  'You can click on each Warcraft Logs badge in this table to view which reports are linked to the event.'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  events: wowaudit.EventBasicReference[]
  isLoading: boolean
  title?: string
  description?: string
  pullCountByEventId?: Record<string, number>
  encounterName?: string
}

const EventInsightsEventsDialog = ({
  open,
  onOpenChange,
  events,
  isLoading,
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  pullCountByEventId,
  encounterName,
}: Props) => {
  const { t } = useTranslation()
  const { team, guild } = useTeam()
  const queryClient = useQueryClient()
  const [syncingEventId, setSyncingEventId] = useState<number | null>(null)
  const hasEnoughPremiumPoints =
    (guild.premiumPoints || 0) >= WCL_REPORT_RETRIEVAL_THRESHOLD
  const hasLinkedWclGuild = !!team.wclGuildId && !!team.wclLinkedAccountId
  const canSync = getAccessLevelForTeam(guild, 'events', team.id) === 'admin'
  const [raidReportsDialogEventId, setRaidReportsDialogEventId] = useState<
    number | undefined
  >(undefined)
  const [raidReportsDialogOpen, setRaidReportsDialogOpen] = useState(false)
  const { data: raidReportsMapResponse, isLoading: isRaidReportsMapLoading } =
    useTeamEventRaidReportsMap(team.id, open)
  const raidReportsByEventId =
    raidReportsMapResponse?.raidReportsByEventId || {}
  const {
    data: raidReportsDialogEvent,
    isLoading: isRaidReportsDialogEventLoading,
  } = useTeamEvent(team.id, raidReportsDialogEventId)
  const { data: raidReports = [], isLoading: isRaidReportsLoading } =
    useEventRaidReports(
      team.id,
      raidReportsDialogEventId,
      raidReportsDialogOpen,
    )

  const onSyncEvent = useCallback(
    async (eventId: number) => {
      setSyncingEventId(eventId)
      try {
        const response = await syncEventRaidReports(team.id, eventId)
        await awaitSuccessfulJob(response.job_id)
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ['teamEventRaidReportsMap'],
          }),
          queryClient.invalidateQueries({ queryKey: ['eventRaidReports'] }),
          queryClient.invalidateQueries({ queryKey: ['raidPlanDeathSources'] }),
          queryClient.invalidateQueries({ queryKey: ['raidPlanDeaths'] }),
          queryClient.invalidateQueries({ queryKey: ['teamEventStatistics'] }),
        ])
        toast.success('Report sync completed')
      } catch (error) {
        displayErrorToast(error as Error | string, t)
      } finally {
        setSyncingEventId(null)
      }
    },
    [queryClient, t, team.id],
  )

  const sortedEvents = useMemo(() => {
    const eventTimestamp = (event: wowaudit.EventBasicReference) =>
      eventInstant(event.startsAt, event.timezone).valueOf()

    return [...events].sort((a, b) => eventTimestamp(b) - eventTimestamp(a))
  }, [events])
  const latestStartedEvent = sortedEvents.find(
    (event) => !eventInstant(event.startsAt, event.timezone).isAfter(dayjs()),
  )

  const columns = useMemo<ColumnDef<wowaudit.EventBasicReference>[]>(
    () => [
      eventTitleColumn<wowaudit.EventBasicReference>(team),
      eventDateColumn<wowaudit.EventBasicReference>(),
      eventTimeColumn<wowaudit.EventBasicReference>(),
      eventRaidReportsColumn<wowaudit.EventBasicReference>({
        raidReportsByEventId,
        isLoading: isRaidReportsMapLoading,
        onOpen: (eventId) => {
          setRaidReportsDialogEventId(eventId)
          setRaidReportsDialogOpen(true)
        },
        pullCountByEventId,
        encounterName,
      }),
      eventReportActionsColumn<wowaudit.EventBasicReference>({
        onView: (eventId) => {
          setRaidReportsDialogEventId(eventId)
          setRaidReportsDialogOpen(true)
        },
        onSync:
          canSync && hasEnoughPremiumPoints && hasLinkedWclGuild
            ? onSyncEvent
            : undefined,
        syncingEventId,
        syncLocked: syncingEventId != null,
      }),
    ],
    [
      canSync,
      hasEnoughPremiumPoints,
      hasLinkedWclGuild,
      isRaidReportsMapLoading,
      encounterName,
      pullCountByEventId,
      raidReportsByEventId,
      onSyncEvent,
      syncingEventId,
    ],
  )

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className='max-w-4xl'
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          {!hasEnoughPremiumPoints ? (
            <PremiumUpsell
              title='Premium feature'
              description='Warcraft Logs data can only be retrieved for teams with enough premium benefits.'
              to={`${team.path}/premium`}
              onNavigate={() => onOpenChange(false)}
            />
          ) : null}
          <DataTable<wowaudit.EventBasicReference, unknown>
            className='whitespace-nowrap'
            columns={columns as ColumnDef<wowaudit.EventBasicReference>[]}
            data={sortedEvents}
            includeHeader={true}
            pagination={10}
            emptyMessage={() =>
              isLoading ? 'Loading events...' : 'No events found'
            }
          />
          {canSync ? (
            <div className='flex justify-end'>
              <Button
                type='button'
                variant='outline'
                size='sm'
                loading={syncingEventId != null}
                disabled={
                  syncingEventId != null ||
                  !latestStartedEvent ||
                  !hasEnoughPremiumPoints ||
                  !hasLinkedWclGuild
                }
                onClick={() => {
                  if (latestStartedEvent) onSyncEvent(latestStartedEvent.id)
                }}
              >
                Sync now
              </Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      <EventRaidReportsDialog
        open={raidReportsDialogOpen}
        onOpenChange={(nextOpen) => {
          setRaidReportsDialogOpen(nextOpen)
          if (!nextOpen) {
            setRaidReportsDialogEventId(undefined)
          }
        }}
        reports={raidReports}
        event={raidReportsDialogEvent}
        isLoading={isRaidReportsLoading || isRaidReportsDialogEventLoading}
      />
    </>
  )
}

export default EventInsightsEventsDialog
